import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

// Lightweight dependency-free WYSIWYG editor for CRM email bodies. Emits HTML
// (paragraphs, links, lists, inline images) so the branded email renders with
// real formatting instead of raw text.
const exec = (cmd, val) => {
  try {
    document.execCommand(cmd, false, val);
  } catch (e) {
    /* ignore unsupported command */
  }
};

const RichTextEditor = forwardRef(({ value, onChange, onUpload }, ref) => {
  const elRef = useRef(null);
  const savedRange = useRef(null);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  // Tracks the last HTML we pushed up so the sync effect can tell our own edits
  // (skip — never touch the DOM) from external changes (template load / clear).
  const lastValue = useRef(value || '');

  // Remove invisible text colors (white / near-white on no background) that some
  // browsers bake in from the dark dashboard theme — they vanish on the white
  // email canvas. White text on a colored element (e.g. the CTA button) is kept.
  const scrubInvisibleColors = root => {
    if (!root) {
      return;
    }
    root.querySelectorAll('[style]').forEach(el => {
      const color = (el.style.color || '').replace(/\s+/g, '').toLowerCase();
      const invisible =
        color === '#fff' ||
        color === '#ffffff' ||
        color === 'white' ||
        color === 'rgb(255,255,255)' ||
        /^rgba\(255,255,255/.test(color);
      const bg = (el.style.backgroundColor || el.style.background || '').trim();
      const hasBackground = bg && bg !== 'transparent' && !/^rgba\(0,0,0,0\)/.test(bg.replace(/\s+/g, ''));
      if (invisible && !hasBackground) {
        el.style.color = '';
        if (!el.getAttribute('style')) {
          el.removeAttribute('style');
        }
      }
    });
  };

  // Seed the initial content once on mount.
  useEffect(() => {
    exec('defaultParagraphSeparator', 'p');
    exec('styleWithCSS', true);
    if (elRef.current) {
      elRef.current.innerHTML = value || '';
      scrubInvisibleColors(elRef.current);
      lastValue.current = elRef.current.innerHTML;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only rewrite the DOM for EXTERNAL value changes (template selected, cleared
  // after send). Rewriting on our own edits collapses the caret and breaks
  // click-to-place.
  useEffect(() => {
    if (elRef.current && (value || '') !== lastValue.current && (value || '') !== elRef.current.innerHTML) {
      elRef.current.innerHTML = value || '';
      scrubInvisibleColors(elRef.current);
      lastValue.current = elRef.current.innerHTML;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = () => {
    if (elRef.current) {
      const html = elRef.current.innerHTML;
      lastValue.current = html;
      onChange(html);
    }
  };

  const handleBlur = () => {
    if (elRef.current) {
      scrubInvisibleColors(elRef.current);
    }
    saveSelection();
    emit();
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && elRef.current && elRef.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    if (!elRef.current) {
      return;
    }
    elRef.current.focus();
    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const run = (cmd, val) => {
    restoreSelection();
    exec(cmd, val);
    saveSelection();
    emit();
  };

  const insertHtmlAtCursor = html => {
    restoreSelection();
    exec('insertHTML', html);
    saveSelection();
    emit();
  };

  useImperativeHandle(ref, () => ({ insertHtml: insertHtmlAtCursor }));

  const addLink = () => {
    saveSelection();
    // eslint-disable-next-line no-alert
    const url = window.prompt('Link URL (https://...):');
    if (!url) {
      return;
    }
    const safe = url.trim();
    restoreSelection();
    const sel = window.getSelection();
    if (sel && sel.toString()) {
      exec('createLink', safe);
    } else {
      const label = safe.replace(/</g, '&lt;').replace(/>/g, '&gt;');
      exec('insertHTML', `<a href="${safe.replace(/"/g, '&quot;')}">${label}</a>`);
    }
    saveSelection();
    emit();
  };

  const pickImage = () => {
    saveSelection();
    imageInputRef.current.click();
  };

  const pickFile = () => {
    saveSelection();
    fileInputRef.current.click();
  };

  const onImageSelected = async event => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    try {
      const res = await onUpload(file);
      if (res && res.url) {
        insertHtmlAtCursor(
          `<p style="margin:0 0 14px;"><img src="${res.url}" alt="" style="display:block;max-width:100%;height:auto;border-radius:8px;" /></p>`
        );
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert('Image upload failed. Please try again.');
    }
  };

  const onFileSelected = async event => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    try {
      const res = await onUpload(file);
      if (res && res.url) {
        const name = (res.filename || file.name || 'attachment').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        insertHtmlAtCursor(`<p style="margin:0 0 14px;">📎 <a href="${res.url}">${name}</a></p>`);
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert('File upload failed. Please try again.');
    }
  };

  const Btn = ({ label, cmd, val, onClick, title }) => (
    <button
      type="button"
      className="crm-rte-btn"
      title={title || label}
      onMouseDown={e => e.preventDefault()}
      onClick={onClick || (() => run(cmd, val))}
    >
      {label}
    </button>
  );

  const FONT_OPTIONS = [
    ['', 'Font'],
    ['Arial, Helvetica, sans-serif', 'Arial'],
    ['Helvetica, Arial, sans-serif', 'Helvetica'],
    ['Verdana, Geneva, sans-serif', 'Verdana'],
    ['Tahoma, Geneva, sans-serif', 'Tahoma'],
    ['"Trebuchet MS", Helvetica, sans-serif', 'Trebuchet MS'],
    ['Georgia, "Times New Roman", serif', 'Georgia'],
    ['"Times New Roman", Times, serif', 'Times New Roman'],
    ['"Courier New", Courier, monospace', 'Courier New'],
  ];

  const applyFont = event => {
    const family = event.target.value;
    event.target.value = '';
    if (!family) {
      return;
    }
    // execCommand fontName only takes a face name; wrap the selection in an
    // inline font-family span so email clients honor the exact stack.
    restoreSelection();
    const sel = window.getSelection();
    if (sel && sel.toString()) {
      const span = `<span style="font-family:${family.replace(/"/g, '&quot;')};">${sel.toString().replace(/</g, '&lt;')}</span>`;
      exec('insertHTML', span);
    } else {
      exec('fontName', family);
    }
    saveSelection();
    emit();
  };

  const applyColor = event => {
    run('foreColor', event.target.value);
  };

  return (
    <div className="crm-rte">
      <div className="crm-rte-toolbar">
        <Btn label="B" cmd="bold" title="Bold" />
        <Btn label="I" cmd="italic" title="Italic" />
        <Btn label="U" cmd="underline" title="Underline" />
        <select
          className="crm-rte-select"
          title="Font"
          defaultValue=""
          onMouseDown={saveSelection}
          onChange={applyFont}
        >
          {FONT_OPTIONS.map(([value, label]) => (
            <option key={label} value={value}>
              {label}
            </option>
          ))}
        </select>
        <label className="crm-rte-color" title="Text color">
          <span aria-hidden="true">A</span>
          <input type="color" defaultValue="#1f2a37" onMouseDown={saveSelection} onChange={applyColor} />
        </label>
        <span className="crm-rte-sep" />
        <Btn label="H" cmd="formatBlock" val="H2" title="Heading" />
        <Btn label="¶" cmd="formatBlock" val="P" title="Paragraph" />
        <Btn label="• List" cmd="insertUnorderedList" title="Bulleted list" />
        <Btn label="1. List" cmd="insertOrderedList" title="Numbered list" />
        <span className="crm-rte-sep" />
        <Btn label="🔗 Link" onClick={addLink} title="Insert link" />
        <Btn label="🖼 Image" onClick={pickImage} title="Upload & insert image" />
        <Btn label="📎 File" onClick={pickFile} title="Attach a file (inserts a download link)" />
        <span className="crm-rte-sep" />
        <Btn label="⨯ Clear" cmd="removeFormat" title="Clear formatting" />
      </div>
      <div
        ref={elRef}
        className="crm-rte-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={handleBlur}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
      />
      <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onImageSelected} />
      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={onFileSelected} />
    </div>
  );
});

export default RichTextEditor;
